/**
 * Zertivo i18n engine v2 — high-performance, multi-language-ready.
 *
 * ──────────────────────────────────────────────────────────────────
 * Architecture
 * ──────────────────────────────────────────────────────────────────
 * - Translations live in /src/i18n/locales/{code}.json
 * - DE + EN are bundled (instant startup).
 * - RO / ES / IT are registered as lazy-loadable — bundle split keeps
 *   initial JS small; they get fetched only when the user switches.
 * - Fallback chain: requested-lang → FALLBACK_LANG (en) → key tail.
 * - Interpolation: `t('hello.user', { name: 'Max' })` with `{{name}}`.
 * - Cookie (primary) + localStorage (quick read) persistence.
 * - URL override: `?lang=ro` forces + persists the locale.
 * - `registerLocale(code, name, dict, flag)` — register at runtime if needed.
 *
 * Performance:
 * - Translations are flat k→v objects; lookup is O(1).
 * - A Set deduplicates missing-key warnings so dev-console stays clean.
 * - The React hook only re-renders when `lang` actually changes.
 */
import de from './locales/de.json';
import en from './locales/en.json';

export const DEFAULT_LANG = 'de';
export const FALLBACK_LANG = 'en';

// Locales bundled at startup. Bundled dicts are non-lazy (instant).
const _bundled = {
  de: { code: 'de', name: 'Deutsch',   flag: '🇩🇪', dict: de, loaded: true },
  en: { code: 'en', name: 'English',   flag: '🇬🇧', dict: en, loaded: true },
};

// Locales registered but not yet loaded (multi-language-ready).
// Dynamic imports keep these out of the critical bundle.
const _lazy = {
  ro: {
    code: 'ro', name: 'Română', flag: '🇷🇴',
    load: () => import(/* webpackChunkName: "locale-ro" */ './locales/ro.json'),
  },
  es: {
    code: 'es', name: 'Español', flag: '🇪🇸',
    load: () => import(/* webpackChunkName: "locale-es" */ './locales/es.json'),
  },
  it: {
    code: 'it', name: 'Italiano', flag: '🇮🇹',
    load: () => import(/* webpackChunkName: "locale-it" */ './locales/it.json'),
  },
};

export const LOCALES = { ..._bundled };

// Expose metadata (without dict) for language picker UIs — includes lazy ones.
export function getLocaleList() {
  const list = [];
  for (const code of Object.keys(_bundled)) {
    list.push({ code, name: _bundled[code].name, flag: _bundled[code].flag });
  }
  for (const code of Object.keys(_lazy)) {
    if (!LOCALES[code]) {
      list.push({ code, name: _lazy[code].name, flag: _lazy[code].flag });
    }
  }
  return list;
}

const missing = new Set();
const _inflight = {};

export function registerLocale(code, name, dict, flag = '') {
  LOCALES[code] = { code, name, flag, dict, loaded: true };
}

/**
 * Ensure a locale's dict is in memory. Returns a Promise.
 * Resolves to `true` if loaded, `false` on failure (falls back to FALLBACK_LANG).
 */
export async function ensureLocaleLoaded(code) {
  if (LOCALES[code]?.loaded) return true;
  if (_lazy[code]) {
    if (_inflight[code]) return _inflight[code];
    _inflight[code] = (async () => {
      try {
        const mod = await _lazy[code].load();
        const dict = mod.default || mod;
        LOCALES[code] = {
          code, name: _lazy[code].name, flag: _lazy[code].flag,
          dict, loaded: true,
        };
        return true;
      } catch (e) {
        // eslint-disable-next-line no-console
        if (process.env.NODE_ENV !== 'production') console.warn(`[i18n] failed to load locale "${code}"`, e);
        return false;
      } finally {
        delete _inflight[code];
      }
    })();
    return _inflight[code];
  }
  return false;
}

const COOKIE_NAME = 'zertivo_lang';
const COOKIE_MAX_AGE = 60 * 60 * 24 * 365; // 1 year

function readCookie(name) {
  try {
    const m = document.cookie.match(new RegExp('(?:^|; )' + name + '=([^;]*)'));
    return m ? decodeURIComponent(m[1]) : null;
  } catch { return null; }
}

function writeCookie(name, value) {
  try {
    const secure = typeof window !== 'undefined' && window.location.protocol === 'https:' ? '; Secure' : '';
    document.cookie = `${name}=${encodeURIComponent(value)}; Max-Age=${COOKIE_MAX_AGE}; Path=/; SameSite=Lax${secure}`;
  } catch {}
}

function isKnown(code) {
  return !!(LOCALES[code] || _lazy[code]);
}

export function detectLang() {
  // Language switcher is disabled app-wide — force DE regardless of any
  // persisted preference, URL override, or browser Accept-Language.
  return DEFAULT_LANG;
}

export function persistLang(code) {
  writeCookie(COOKIE_NAME, code);
  try { localStorage.setItem('zertivo_lang', code); } catch {}
}

/**
 * Core translate function — pure, synchronous.
 * @param {string} lang  Current locale code
 * @param {string} key   Dot-notation translation key
 * @param {object} [vars] Interpolation variables
 */
export function translate(lang, key, vars) {
  if (!key) return '';
  const current = LOCALES[lang]?.dict || {};
  const fallback = LOCALES[FALLBACK_LANG]?.dict || {};
  let value =
    current[key] !== undefined ? current[key] :
    fallback[key] !== undefined ? fallback[key] :
    null;

  if (value === null) {
    const id = `${lang}:${key}`;
    if (!missing.has(id) && process.env.NODE_ENV !== 'production') {
      missing.add(id);
      // eslint-disable-next-line no-console
      console.warn(`[i18n] Missing key "${key}" for locale "${lang}"`);
    }
    value = '';
  }

  if (vars && typeof value === 'string') {
    value = value.replace(/{{\s*(\w+)\s*}}/g, (_, k) => (vars[k] !== undefined ? String(vars[k]) : `{{${k}}}`));
  }
  return value;
}
